import { Box, SkeletonRectangle, Text, Tooltip } from '@wix/design-system';
import { CycleId, CycleUtils, GEOS } from '@wix/package-picker-shared';
import {
  CycleSelectorBadge,
  CycleSelectorCheck,
  CycleSelectorDisabledFeatures,
  eclipseServerData,
  ECLIPSE_PRIMARY_COLOR,
  FreeDomainMatchCycleLabel,
  useCurrentModule,
  useCycleAvailability,
  useCycleSelectorStrikeout,
  useSelectedCycle,
} from '@wix/package-picker-shared-eclipse';
import { CycleSelector as PlatformCycleSelector } from '@wix/premium-purchase-platform/@components';
import React from 'react';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import { BrazilCyclePrice } from './BrazilCyclePrice';
import s from './CycleSelectorItem.scss';
import { useCycleSelectorItemTooltip } from './useCycleSelectorItemTooltip';

export const CycleSelectorItem = ({
  productId,
  cycleId,
  isUpgradeFlow,
}: {
  productId: string;
  cycleId: CycleId;
  isUpgradeFlow: boolean;
}) => {
  const selectedCycle = useSelectedCycle();

  const { data: cyclePricingData, isLoading: isFetchCycleLoading } =
    EclipsePackagePickerModule.SDK.eclipseOfferingApiClient.hooks.useFetchCyclePricing(
      { productId, cycleId },
      Boolean(productId && cycleId),
    );

  // Used to fetch data to prevent loading state when the component is mounted
  EclipsePackagePickerModule.SDK.eclipseOfferingApiClient.hooks.useFetchMonthlyDisabledFeatures(
    { productId },
    Boolean(productId),
  );

  const { data: product, isLoading: isProductLoading } =
    EclipsePackagePickerModule.SDK.eclipseOfferingApiClient.hooks.useFetchProduct(
      { productId },
      Boolean(productId),
    );

  const { cyclePricing } = cyclePricingData ?? {};

  const { cycle, monthlyFinalPrice, discountFixedAmount, savings } =
    cyclePricing ?? {};

  const { hasSale } = product ?? {};

  const module = useCurrentModule();
  const { experiments } = module.internals.experiments.hooks.useExperiments();
  const { Trans } = module.internals.useTranslations();

  const strikeoutPrice = useCycleSelectorStrikeout(productId, cycleId);

  const isBrazilCyclePrice =
    experiments.isEnabled('specs.premium.BrazilCyclePricing') &&
    eclipseServerData.get().geo === GEOS.BRAZIL &&
    cycle &&
    !CycleUtils.isMonthly(cycle);

  const handleSelectCycle = (_cycleId: CycleId) => {
    EclipsePackagePickerModule.SDK.PackagePickerStore.setSelectedCycleId(
      _cycleId,
    );

    EclipsePackagePickerModule.internals.bi.onSelectCycle?.({
      cycle,
      productId,
    });
  };

  const { isCycleAvailable, isLoading: isCycleAvailabilityLoading } =
    useCycleAvailability(productId, cycle);

  const tooltipData = useCycleSelectorItemTooltip({ cycle, productId });

  const isSelected = selectedCycle
    ? CycleUtils.isCycleEqual(selectedCycle, cycle)
    : false;

  const isDisabled = !isCycleAvailabilityLoading && !isCycleAvailable;

  const selectedProps = {
    border: `1px solid ${ECLIPSE_PRIMARY_COLOR}`,
    backgroundColor: '#FAF9FF',
  };

  const disabledProps = {
    background: 'white',
    opacity: isDisabled ? 0.6 : 1,
    color: '#ACAFC4 !important',
  };

  const isYearlyCycle = cycle?.cycleDuration?.unit === 'YEAR';
  const yearCount = cycle?.cycleDuration?.count ?? 0;
  const isCycleSelectorSavingsTooltipEnabled = experiments.isEnabled(
    'cycleSelectorSavingsTooltip',
  );
  const showSavingsTooltip =
    isCycleSelectorSavingsTooltipEnabled &&
    isSelected &&
    isYearlyCycle &&
    Boolean(hasSale) &&
    !isUpgradeFlow &&
    Boolean(savings?.fixedAmount?.amount) &&
    Boolean(discountFixedAmount?.amount);
  const isMatchDomainCycleEnabled = experiments.isEnabled(
    'matchDomainCycleToPremiumCycle',
  );
  const showFreeDomainLabel =
    isMatchDomainCycleEnabled && isYearlyCycle && !isDisabled;
  const cardPadding =
    isMatchDomainCycleEnabled && isYearlyCycle ? 'SP2 SP3' : 'SP3';

  const isLoading = isFetchCycleLoading || isProductLoading;

  if (isLoading) {
    return (
      <div style={{ position: 'relative' }}>
        <Box align="center" width="100%" textAlign="center">
          <SkeletonRectangle height="62px" width="700px" />
        </Box>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {showSavingsTooltip && (
        <div className={s.savingsTooltip}>
          <Box
            backgroundColor="white"
            borderRadius="6px"
            padding="9px 12px"
            minWidth="170px"
            maxWidth="230px"
          >
            <Text size="small" maxLines={3}>
              <Trans
                i18nKey="cycle_entry.savings_tooltip"
                values={{
                  amount: savings?.fixedAmount?.formattedAmount,
                  saleAmount: discountFixedAmount?.formattedAmount,
                }}
                components={{
                  1: <strong className={s.savingsAmount} />,
                  3: <strong className={s.savingsAmount} />,
                }}
              />
            </Text>
          </Box>
        </div>
      )}
    <Tooltip
      dataHook="cycle-selector-disabled-tooltip"
      disabled={!tooltipData?.tooltipContent}
      content={tooltipData?.tooltipContent}
      placement="left"
      moveBy={{ x: 12, y: 0 }}
      onShow={tooltipData?.onShowTooltip}
    >
      <PlatformCycleSelector.Card.Root
        className={isCycleSelectorSavingsTooltipEnabled && !isSelected && !isDisabled ? s.hoverableCard : undefined}
        key={cycleId}
        padding={cardPadding}
        isSelected={isSelected}
        onSelect={() => handleSelectCycle(cycleId)}
        borderRadius="6px"
        disabled={isDisabled}
        {...(isDisabled ? { ...disabledProps } : {})}
        collapseContent={
          cycle && CycleUtils.isMonthly(cycle) ? (
            <Box padding="SP4 SP2 SP3 32px">
              <CycleSelectorDisabledFeatures productId={productId} />
            </Box>
          ) : null
        }
        isOpen={isSelected}
        {...(isSelected ? { ...selectedProps } : {})}
      >
        <PlatformCycleSelector.Card.Content
          {...(showFreeDomainLabel
            ? { direction: 'horizontal' as const, gap: 'SP2' as const }
            : {})}
          verticalAlign="middle"
          align="space-between"
        >
          <Box
            {...(showFreeDomainLabel
              ? { flex: '1' as const, minWidth: '0' }
              : { width: '100%' })}
            verticalAlign="middle"
          >
            <Box paddingRight="SP2">
              <CycleSelectorCheck
                isSelected={isSelected}
                disabled={isDisabled}
              />
            </Box>
            <Box className={s.cycleName} minWidth="90px" maxWidth="200px">
              <PlatformCycleSelector.Card.Cycle
                cycle={cycle}
                paddingRight="SP2"
                fontWeight="500"
              />
            </Box>
            {isBrazilCyclePrice ? (
              <Box
                minWidth="180px"
                maxWidth="400px"
                width="100%"
                paddingLeft="6px"
              >
                <BrazilCyclePrice productId={productId} cycleId={cycleId} />
              </Box>
            ) : (
              <PlatformCycleSelector.Card.Price
                price={monthlyFinalPrice?.formattedAmount ?? undefined}
                fullPrice={strikeoutPrice}
                durationCount={cycle?.cycleDuration?.count}
                durationUnit={cycle?.cycleDuration?.unit}
                minWidth="180px"
                maxWidth="400px"
                width="100%"
                cycleSavings={
                  hasSale && !isDisabled
                    ? discountFixedAmount?.formattedAmount
                    : undefined
                }
                cycleSavingsTextProps={{
                  className: s.cycleSavingsInPrice,
                }}
              />
            )}
          </Box>

          <Box
            direction="vertical"
            align="right"
            gap="4px"
            {...(showFreeDomainLabel ? { className: s.rightColumn } : {})}
          >
            {cycleId && !isDisabled && (
              <CycleSelectorBadge productId={productId} cycleId={cycleId} />
            )}
            {showFreeDomainLabel && (
              <FreeDomainMatchCycleLabel
                yearCount={yearCount}
                rowClassName={s.freeDomainRow}
                textClassName={s.freeDomainLabel}
              />
            )}
          </Box>
        </PlatformCycleSelector.Card.Content>
      </PlatformCycleSelector.Card.Root>
    </Tooltip>
    </div>
  );
};
